import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { LocalizedText } from '@/utils/localization';

export const STRATEGIC_VISION_MAX_LENGTH = 150;

const EMPTY_VISION: LocalizedText = {};

/**
 * Drops blank entries and trims the remaining ones so the saved payload never
 * carries an empty translation, and so two dictionaries can be compared safely.
 */
export const normalizeStrategicVision = (vision: LocalizedText): LocalizedText => {
    return Object.keys(vision)
        .sort()
        .reduce<LocalizedText>((accumulator, language) => {
            const trimmed = vision[language].trim();

            if (trimmed) {
                accumulator[language] = trimmed;
            }

            return accumulator;
        }, {});
};

interface UseStrategicVisionDraftParams {
    savedVision: LocalizedText | undefined;
    currentLanguage: string;
}

export interface StrategicVisionDraft {
    /** The full draft dictionary, current language included. */
    draft: LocalizedText;
    /** The draft text for the language the admin is currently working in. */
    currentText: string;
    setCurrentText: (text: string) => void;
    /** Replaces the whole dictionary — used when the translations modal saves. */
    replaceDraft: (vision: LocalizedText) => void;
    /** The payload to send: trimmed, blank languages removed. */
    payload: LocalizedText;
    isDirty: boolean;
}

/**
 * Holds the strategic vision being edited, kept in sync with the loaded value.
 */
export const useStrategicVisionDraft = ({
    savedVision,
    currentLanguage,
}: UseStrategicVisionDraftParams): StrategicVisionDraft => {
    const [draft, setDraft] = useState<LocalizedText>(savedVision ?? EMPTY_VISION);

    // The saved vision is compared by content, not by reference: a caller re-creating the
    // dictionary on every render (`?? {}`) must not reset the draft the admin is typing in.
    const savedKey = useMemo(
        () => JSON.stringify(normalizeStrategicVision(savedVision ?? EMPTY_VISION)),
        [savedVision],
    );
    // Kept in a ref, written from an effect rather than during render, so the sync effect below
    // can read the latest dictionary without depending on its identity.
    const latestSavedVision = useRef(savedVision);

    useEffect(() => {
        latestSavedVision.current = savedVision;
    }, [savedVision]);

    useEffect(() => {
        setDraft(latestSavedVision.current ?? EMPTY_VISION);
    }, [savedKey]);

    const setCurrentText = useCallback(
        (text: string) => {
            setDraft((previous) => ({ ...previous, [currentLanguage]: text }));
        },
        [currentLanguage],
    );

    const replaceDraft = useCallback((vision: LocalizedText) => {
        setDraft({ ...vision });
    }, []);

    const payload = useMemo(() => normalizeStrategicVision(draft), [draft]);

    const isDirty = JSON.stringify(payload) !== savedKey;

    return {
        draft,
        currentText: draft[currentLanguage] ?? '',
        setCurrentText,
        replaceDraft,
        payload,
        isDirty,
    };
};
